import React, { useCallback, useMemo, useState } from 'react';
import {
  type DoseSpotPharmacySearchParams,
  type DoseSpotPharmacySearchResult,
  useLazySearchDoseSpotPharmaciesQuery,
} from '@features/eprescriptions/store/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { US_STATES } from '@shared/constants/states';
import { useSnackbar } from 'notistack';

import {
  type AddPatientPharmacyRequest,
  type PatientPharmacyInfo,
  useAddPatientPharmacyMutation,
  usePatientPharmaciesQuery,
  useRemovePatientPharmacyMutation,
  useUpdatePatientPharmacyMutation,
} from '../store/api';

import { SectionHeader } from './PatientInfoModal/shared';

interface PatientPharmaciesSectionProps {
  patientId: string;
  source: string;
  compact?: boolean;
  canEdit?: boolean;
}

interface PharmacyFormState {
  name: string;
  pharmacy_type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
  dosespot_pharmacy_id: string;
  is_primary: boolean;
}

interface PharmacySearchFormState {
  name: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY_FORM: PharmacyFormState = {
  name: '',
  pharmacy_type: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
  dosespot_pharmacy_id: '',
  is_primary: false,
};

const EMPTY_SEARCH_FORM: PharmacySearchFormState = {
  name: '',
  city: '',
  state: '',
  zip: '',
};

const addButtonSx = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 500,
  p: '6px 0',
  minWidth: 0,
} as const;

const cardActionButtonSx = {
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.2,
  minWidth: 0,
  p: 0,
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
} as const;

const trimOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const pharmacySearchResultToForm = (
  pharmacy: DoseSpotPharmacySearchResult,
  isPrimary: boolean,
): PharmacyFormState => ({
  name: pharmacy.store_name,
  pharmacy_type: pharmacy.pharmacy_specialties?.[0] ?? '',
  address_line1: pharmacy.address1 ?? '',
  address_line2: pharmacy.address2 ?? '',
  city: pharmacy.city ?? '',
  state: pharmacy.state ?? '',
  zip: pharmacy.zip_code ?? '',
  phone: pharmacy.primary_phone ?? '',
  fax: pharmacy.primary_fax ?? '',
  dosespot_pharmacy_id: pharmacy.pharmacy_id,
  is_primary: isPrimary,
});

const formToAddRequest = (form: PharmacyFormState): AddPatientPharmacyRequest => ({
  name: form.name.trim(),
  pharmacy_type: trimOrNull(form.pharmacy_type),
  address_line1: trimOrNull(form.address_line1),
  address_line2: trimOrNull(form.address_line2),
  city: trimOrNull(form.city),
  state: trimOrNull(form.state),
  zip: trimOrNull(form.zip),
  phone: trimOrNull(form.phone),
  fax: trimOrNull(form.fax),
  dosespot_pharmacy_id: trimOrNull(form.dosespot_pharmacy_id),
  is_primary: form.is_primary,
});

const formatAddress = (pharmacy: PatientPharmacyInfo): string => {
  const cityStateZip = [pharmacy.city, pharmacy.state, pharmacy.zip].filter(Boolean).join(', ');
  return [pharmacy.address_line1, pharmacy.address_line2, cityStateZip].filter(Boolean).join(' · ');
};

const formatSearchResultAddress = (pharmacy: DoseSpotPharmacySearchResult): string => {
  const cityStateZip = [pharmacy.city, pharmacy.state, pharmacy.zip_code]
    .filter(Boolean)
    .join(', ');
  return [pharmacy.address1, pharmacy.address2, cityStateZip].filter(Boolean).join(' · ');
};

const buildSearchParams = (searchForm: PharmacySearchFormState): DoseSpotPharmacySearchParams => {
  const params: DoseSpotPharmacySearchParams = {};
  const name = searchForm.name.trim();
  const city = searchForm.city.trim();
  const state = searchForm.state.trim();
  const zip = searchForm.zip.trim();
  if (name.length >= 3) params.name = name;
  if (city.length >= 3) params.city = city;
  if (state.length === 2) params.state = state;
  if (zip.length >= 3) params.zip = zip;
  return params;
};

export const PatientPharmaciesSection: React.FC<PatientPharmaciesSectionProps> = ({
  patientId,
  source,
  compact = false,
  canEdit = true,
}) => {
  const { data, isLoading, isError, refetch } = usePatientPharmaciesQuery(
    { patientId },
    { skip: !patientId },
  );
  const [addPharmacy, { isLoading: isAdding }] = useAddPatientPharmacyMutation();
  const [updatePharmacy] = useUpdatePatientPharmacyMutation();
  const [removePharmacy, { isLoading: isRemoving }] = useRemovePatientPharmacyMutation();
  const [
    searchDoseSpotPharmacies,
    { data: pharmacySearchData, isFetching: isSearching, isError: isSearchError },
  ] = useLazySearchDoseSpotPharmaciesQuery();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PharmacyFormState>(EMPTY_FORM);
  const [searchForm, setSearchForm] = useState<PharmacySearchFormState>(EMPTY_SEARCH_FORM);
  const [hasSearched, setHasSearched] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const tracking = useEventTracking();

  const pharmacies = useMemo(() => data?.pharmacies ?? [], [data?.pharmacies]);
  const pharmacySearchResults = useMemo(
    () => pharmacySearchData?.pharmacies ?? [],
    [pharmacySearchData?.pharmacies],
  );
  const isSaving = isAdding;
  const searchParams = useMemo(() => buildSearchParams(searchForm), [searchForm]);
  const canSearch = Object.keys(searchParams).length > 0;

  const sortedPharmacies = useMemo(
    () =>
      [...pharmacies].sort((a, b) => Number(b.is_primary ?? false) - Number(a.is_primary ?? false)),
    [pharmacies],
  );

  const resetForm = useCallback(() => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setSearchForm(EMPTY_SEARCH_FORM);
    setHasSearched(false);
  }, []);

  const startAdd = useCallback(() => {
    setForm(EMPTY_FORM);
    setSearchForm(EMPTY_SEARCH_FORM);
    setHasSearched(false);
    setShowForm(true);
  }, []);

  const setFormField = useCallback((field: keyof PharmacyFormState, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]:
        field === 'state' && typeof value === 'string'
          ? value
              .replace(/[^A-Za-z]/g, '')
              .toUpperCase()
              .slice(0, 2)
          : value,
    }));
  }, []);

  const setSearchField = useCallback((field: keyof PharmacySearchFormState, value: string) => {
    setHasSearched(false);
    setSearchForm(prev => ({
      ...prev,
      [field]:
        field === 'state'
          ? value
              .replace(/[^A-Za-z]/g, '')
              .toUpperCase()
              .slice(0, 2)
          : value,
    }));
  }, []);

  const handleSearch = useCallback(async () => {
    if (!canSearch) return;
    setHasSearched(true);
    await searchDoseSpotPharmacies(searchParams);
  }, [canSearch, searchDoseSpotPharmacies, searchParams]);

  const handleAddResult = useCallback(
    async (pharmacy: DoseSpotPharmacySearchResult) => {
      const pharmacyForm = pharmacySearchResultToForm(pharmacy, form.is_primary);
      try {
        await addPharmacy({
          patientId,
          addPatientPharmacyRequest: formToAddRequest(pharmacyForm),
        }).unwrap();
        tracking.patient.infoClinicalItemAdded({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
        resetForm();
      } catch {
        enqueueSnackbar('Failed to save pharmacy', { variant: 'error' });
      }
    },
    [addPharmacy, enqueueSnackbar, form.is_primary, patientId, resetForm, source, tracking],
  );

  const handleRemove = useCallback(
    async (pharmacy: PatientPharmacyInfo) => {
      const confirmed = window.confirm(`Remove ${pharmacy.name} from this patient?`);
      if (!confirmed) return;

      setRemovingId(pharmacy.patient_pharmacy_id);
      try {
        await removePharmacy({
          patientId,
          pharmacyId: pharmacy.patient_pharmacy_id,
        }).unwrap();
        tracking.patient.infoClinicalItemRemoved({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
      } catch {
        enqueueSnackbar('Failed to remove pharmacy', { variant: 'error' });
      } finally {
        setRemovingId(null);
      }
    },
    [enqueueSnackbar, patientId, removePharmacy, source, tracking],
  );

  const makePreferred = useCallback(
    async (pharmacy: PatientPharmacyInfo) => {
      try {
        await updatePharmacy({
          patientId,
          pharmacyId: pharmacy.patient_pharmacy_id,
          // Backend PATCH uses Pydantic model_fields_set, so omitted fields
          // remain untouched and only explicit nulls clear values.
          updatePatientPharmacyRequest: { is_primary: true },
        }).unwrap();
        tracking.patient.infoClinicalItemUpdated({
          patient_id: patientId,
          source,
          item_type: 'pharmacy',
        });
      } catch {
        enqueueSnackbar('Failed to update preferred pharmacy', { variant: 'error' });
      }
    },
    [enqueueSnackbar, patientId, source, tracking, updatePharmacy],
  );

  return (
    <Box sx={{ mb: compact ? 0 : 2 }}>
      {!compact && <SectionHeader>PHARMACIES</SectionHeader>}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {isError && (
        <Box sx={{ py: 1 }}>
          <Button size="small" onClick={() => refetch()} sx={{ textTransform: 'none' }}>
            Retry
          </Button>
        </Box>
      )}

      {!isLoading && !isError && sortedPharmacies.length === 0 && !showForm && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No pharmacies on file
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {sortedPharmacies.map(pharmacy => {
          const address = formatAddress(pharmacy);
          return (
            <Box
              key={pharmacy.patient_pharmacy_id}
              sx={{
                containerType: 'inline-size',
                borderBottom: '1px solid',
                borderColor: 'divider',
                py: 0.75,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'flex-start',
                  gap: 1,
                  '@container (max-width: 360px)': {
                    gridTemplateColumns: '1fr',
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                      {pharmacy.name}
                    </Typography>
                    {pharmacy.is_primary && (
                      <Chip size="small" variant="soft" color="primary" label="Preferred" />
                    )}
                  </Box>
                  {address && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                    >
                      {address}
                    </Typography>
                  )}
                  {(pharmacy.phone || pharmacy.fax) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {[
                        pharmacy.phone && `P ${pharmacy.phone}`,
                        pharmacy.fax && `F ${pharmacy.fax}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.25,
                    pt: 0.25,
                    '@container (max-width: 360px)': {
                      justifyContent: 'flex-start',
                      pt: 0,
                    },
                  }}
                >
                  {canEdit && !pharmacy.is_primary && (
                    <Tooltip title="Make preferred">
                      <IconButton
                        size="small"
                        onClick={() => makePreferred(pharmacy)}
                        sx={{ p: 0.25 }}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEdit && (
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      disabled={isRemoving && removingId === pharmacy.patient_pharmacy_id}
                      sx={cardActionButtonSx}
                      onClick={() => handleRemove(pharmacy)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {showForm && (
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'minmax(180px, 2fr) minmax(150px, 1fr) minmax(96px, 120px)',
              },
              gap: 1,
            }}
          >
            <TextField
              label="Pharmacy name"
              size="small"
              value={searchForm.name}
              onChange={event => setSearchField('name', event.target.value)}
            />
            <TextField
              label="City"
              size="small"
              value={searchForm.city}
              onChange={event => setSearchField('city', event.target.value)}
            />
            <TextField
              select
              label="State"
              size="small"
              value={searchForm.state}
              onChange={event => setSearchField('state', event.target.value)}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {Object.entries(US_STATES).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {code} - {name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'minmax(120px, 220px) auto minmax(180px, 1fr) auto',
              },
              alignItems: 'center',
              gap: 1,
            }}
          >
            <TextField
              label="ZIP"
              size="small"
              value={searchForm.zip}
              onChange={event => setSearchField('zip', event.target.value)}
              sx={{ maxWidth: { sm: 220 } }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={!canSearch || isSearching}
              onClick={handleSearch}
              sx={{ textTransform: 'none', minWidth: 96, minHeight: 40 }}
            >
              {isSearching ? <CircularProgress size={16} /> : 'Search'}
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_primary}
                  onChange={event => setFormField('is_primary', event.target.checked)}
                />
              }
              label="Add as preferred"
              sx={{ m: 0 }}
            />
            <Button size="small" variant="text" onClick={resetForm} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </Box>

          {isSearchError && <Alert severity="error">Failed to search pharmacies</Alert>}

          {hasSearched && !isSearching && pharmacySearchResults.length > 0 && (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              {pharmacySearchResults.map((result, index) => {
                const resultAddress = formatSearchResultAddress(result);
                return (
                  <Box
                    key={result.pharmacy_id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderBottom: index === pharmacySearchResults.length - 1 ? 0 : '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                        {result.store_name}
                      </Typography>
                      {resultAddress && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {resultAddress}
                        </Typography>
                      )}
                      {(result.primary_phone || result.primary_fax) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {[
                            result.primary_phone && `P ${result.primary_phone}`,
                            result.primary_fax && `F ${result.primary_fax}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        disabled={isSaving}
                        onClick={() => handleAddResult(result)}
                        sx={{ textTransform: 'none', minWidth: 96 }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {hasSearched && !isSearching && !isSearchError && pharmacySearchResults.length === 0 && (
            <Alert severity="info">
              Pharmacy not listed? Contact JotPsych support and we will help verify it.
            </Alert>
          )}
        </Box>
      )}

      {canEdit && !showForm && (
        <Button size="small" onClick={startAdd} sx={addButtonSx}>
          + Add pharmacy
        </Button>
      )}
    </Box>
  );
};
